<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { useRoute } from 'vue-router';
    import { useCtScanStore } from '@/views/dicoms/ctScan/ctScanStore';
    import DicomViewerSlider from '@/views/dicoms/ctScan/DicomViewerSlider.vue';
    import CatStackImage from '@/views/dicoms/ctScan/CatStackImage.vue';
    import DicomViewerLayout from '@/views/dicoms/ctScan/DicomViewerLayout.vue';
    import { caseIdFromRoute } from '@/router';

    const store = useCtScanStore();
    const route = useRoute();

    store.$reset();

    const caseId = caseIdFromRoute(route);

    await store.load(caseId);

    if (store.isError) {
        throw new Error('Could not load cat-stacks');
    }

    const sliceSampling = 2;

    const tabs = ref('axial-tab');

    /**
     * Axial slices are ordered feet to head, opposite to the appearance of the orthogonal slices
     * therefore, we invert the axial index when calculating the indicator line in the orthogonal slices
     */
    const axialIndicatorOffset = computed(() => {
        const axial = store.catStacks.axial;
        return axial ? (axial.count - axial.currentIndex) * sliceSampling : 0;
    });

    const coronalIndicatorOffset = computed(() => {
        const coronal = store.catStacks.coronal;
        return (coronal?.currentIndex ?? 0) * sliceSampling;
    });

    const sagittalIndicatorOffset = computed(() => {
        const sagittal = store.catStacks.sagittal;
        return (sagittal?.currentIndex ?? 0) * sliceSampling;
    });
</script>

<template>
    <div class="cat-stack-container">
        <div class="catstack-tabs-title">
            <v-tabs v-model="tabs" align-tabs="start" class="catstack-tabs">
                <v-tab key="axial" value="axial-tab">Axial</v-tab>
                <v-tab key="coronal" value="coronal-tab">Coronal</v-tab>
                <v-tab key="sagittal" value="sagittal-tab">Sagittal</v-tab>
            </v-tabs>
        </div>

        <div class="catstack-content">
            <v-window
                v-if="store.axial && store.coronal && store.sagittal"
                v-model="tabs"
                class="catstack-window"
            >
                <v-window-item key="axial" value="axial-tab">
                    <DicomViewerLayout>
                        <template #main="{ width, height }">
                            <CatStackImage
                                :container-width="width"
                                :container-height="height"
                                :value="store.axial"
                            />
                        </template>
                        <template #slider>
                            <DicomViewerSlider :value="store.axial" />
                        </template>
                        <template #second="{ width, height, padding }">
                            <CatStackImage
                                :container-width="width"
                                :container-height="height"
                                :value="store.coronal"
                                :padding="padding"
                                indicator-line="horizontal"
                                :indicator-offset="axialIndicatorOffset"
                            />
                        </template>
                        <template #third="{ width, height, padding }">
                            <CatStackImage
                                :container-width="width"
                                :container-height="height"
                                :value="store.sagittal"
                                :padding="padding"
                                indicator-line="horizontal"
                                :indicator-offset="axialIndicatorOffset"
                            />
                        </template>
                    </DicomViewerLayout>
                </v-window-item>
                <v-window-item key="coronal" value="coronal-tab">
                    <DicomViewerLayout>
                        <template #main="{ width, height }">
                            <CatStackImage
                                :container-width="width"
                                :container-height="height"
                                :value="store.coronal"
                            />
                        </template>
                        <template #slider>
                            <DicomViewerSlider :value="store.coronal" />
                        </template>
                        <template #second="{ width, height, padding }">
                            <CatStackImage
                                :container-width="width"
                                :container-height="height"
                                :value="store.axial"
                                :padding="padding"
                                indicator-line="horizontal"
                                :indicator-offset="coronalIndicatorOffset"
                            />
                        </template>
                        <template #third="{ width, height, padding }">
                            <CatStackImage
                                :container-width="width"
                                :container-height="height"
                                :value="store.sagittal"
                                :padding="padding"
                                indicator-line="vertical"
                                :indicator-offset="coronalIndicatorOffset"
                            />
                        </template>
                    </DicomViewerLayout>
                </v-window-item>
                <v-window-item key="sagittal" value="sagittal-tab">
                    <DicomViewerLayout>
                        <template #main="{ width, height }">
                            <CatStackImage
                                :container-width="width"
                                :container-height="height"
                                :value="store.sagittal"
                            />
                        </template>
                        <template #slider>
                            <DicomViewerSlider :value="store.sagittal" />
                        </template>
                        <template #second="{ width, height, padding }">
                            <CatStackImage
                                :container-width="width"
                                :container-height="height"
                                :value="store.axial"
                                :padding="padding"
                                indicator-line="vertical"
                                :indicator-offset="sagittalIndicatorOffset"
                            />
                        </template>
                        <template #third="{ width, height, padding }">
                            <CatStackImage
                                :container-width="width"
                                :container-height="height"
                                :value="store.coronal"
                                :padding="padding"
                                indicator-line="vertical"
                                :indicator-offset="sagittalIndicatorOffset"
                            />
                        </template>
                    </DicomViewerLayout>
                </v-window-item>
            </v-window>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .cat-stack-container {
        display: flex;
        flex-direction: column;
        width: fit-content;
    }

    .catstack-tabs-title {
        padding: 0 !important;
        border: 1px solid rgb(var(--v-theme-on-increment-grey));
        border-radius: 5px 5px 0 0;

        .catstack-tabs {
            :deep(.v-slide-group__container) {
                padding: 0;
            }

            :deep(.v-slide-group__content) {
                display: flex;
                width: 100%;
                justify-content: space-evenly;
            }

            :deep(.v-tab) {
                text-transform: capitalize;
                font-size: 18px;
                margin: 0;
                flex: 1;
                max-width: none;

                &.v-tab--selected {
                    color: rgb(var(--v-theme-primary));

                    .v-tab__slider {
                        height: 2px;
                        background-color: rgb(var(--v-theme-primary));
                    }
                }
            }
        }
    }

    .catstack-window {
        width: fit-content;

        :deep(.v-window-item) {
            background-color: rgb(var(--v-theme-black));
            width: min-content;
            display: flex;
            flex-direction: column;
        }
    }
</style>
